/**
 * Passage-set REVIEW dropdown (tabbed) — the official TestNav per-passage-set
 * review panel. Scoped to the CURRENT passage set only (locked-flow rule: you
 * may move between the questions/screens of the passage you're on and nowhere
 * else).
 *
 * Reuses the legacy question-navigator markup/CSS so it looks identical to the
 * existing Review dropdown:
 *   - tab header  : ``digitalSHSATModule__navigatorHeader`` / ``__navigatorHeaderFilter``
 *   - count badges: ``__navigatorIconRec`` (All), ``__navigatorIconCir`` (Not
 *                   Answered — orange), ``__navigatorIconMark`` (Bookmarks ribbon)
 *   - rows        : ``__navigatorBodyContent`` (+``--active`` for current),
 *                   orange not-answered dot ``__navigatorQuestionCir``,
 *                   bookmark ribbon ``__navigatorBookmark`` + ``__reviewBox--activeIcon``
 *
 * Three tabs (All Questions / Not Answered / Bookmarks) with counts over the
 * CURRENT set only. Entries: an optional leading "ELA Rdg Comp Directions" row
 * (first reading set only), the set's question rows (numbered 1..N within the
 * set), and (once reached) the "Passage End Directions" / "End of Section" end
 * rows. Every row is CLICKABLE and jumps to its screen within the current set
 * via ``onJump`` (the parent owns the actual navigation); the directions/end
 * info rows are dark and highlight when the student is on that screen.
 *
 * Prop-driven so both flows feed their own data (adaptive: server passageSlots;
 * legacy: the current passage's questions sliced client-side). Ephemeral
 * bookmark state lives in the parent and is passed in per entry.
 */

import React from 'react';
import { FaBookmark } from 'react-icons/fa6';

export type ReviewTab = 'All Questions' | 'Not Answered' | 'Bookmarks';
export const REVIEW_TABS: ReviewTab[] = ['All Questions', 'Not Answered', 'Bookmarks'];

export type PassageReviewEntry = {
  /** Jump target for question rows (adaptive slotNumber or legacy questionNum);
   * for directions/end rows it's only a React key — the parent routes off
   * ``target``. */
  key: number;
  label: string;
  /** ``question`` rows are counted; ``directions`` (leading "ELA Rdg Comp
   * Directions", first reading set only) and ``end`` (Passage End Directions /
   * End of Section) rows are dark and excluded from the counts. All clickable. */
  kind: 'directions' | 'question' | 'end';
  /** For directions/end rows: which screen to navigate to. */
  target?: 'reading-directions' | 'passage-end' | 'end-of-section';
  /** questions only — drives the orange not-answered dot. */
  answered?: boolean;
  /** questions only — drives the bookmark ribbon. */
  bookmarked?: boolean;
  isCurrent: boolean;
};

// Dark styling for the end rows (Passage End Directions / End of Section) —
// clickable (pointer), distinct from the question rows.
const DARK_ROW_STYLE: React.CSSProperties = {
  background: 'linear-gradient(to bottom, #2f2f2f, #4a4a4a)',
  color: '#fff',
  cursor: 'pointer',
  fontWeight: 600,
};

export function PassageReviewList(props: {
  entries: PassageReviewEntry[];
  activeTab: ReviewTab;
  onTabChange: (tab: ReviewTab) => void;
  onJump: (entry: PassageReviewEntry) => void;
}): JSX.Element {
  const { entries, activeTab, onTabChange, onJump } = props;

  // Counts reflect the current set's QUESTIONS only.
  const questions = entries.filter((e) => e.kind === 'question');
  const allCount = questions.length;
  const notAnsweredCount = questions.filter((e) => !e.answered).length;
  const bookmarkCount = questions.filter((e) => e.bookmarked).length;

  // Tab filtering: All shows every entry (incl. end rows); the filtered tabs
  // show only the matching question rows.
  const visible = entries.filter((e) => {
    if (activeTab === 'All Questions') return true;
    if (e.kind !== 'question') return false;
    if (activeTab === 'Not Answered') return !e.answered;
    return !!e.bookmarked; // Bookmarks
  });

  return (
    <div className="digitalSHSATModule__reviewNavigator">
      <div className="digitalSHSATModule__navigatorHeader">
        {REVIEW_TABS.map((tab) => (
          <div
            key={tab}
            className={
              activeTab === tab
                ? 'digitalSHSATModule__navigatorHeaderFilter--active'
                : 'digitalSHSATModule__navigatorHeaderFilter'
            }
            onClick={() => onTabChange(tab)}
          >
            {tab === 'All Questions' && (
              <div className="digitalSHSATModule__navigatorIconRec">{allCount}</div>
            )}
            {tab === 'Not Answered' && (
              <div className="digitalSHSATModule__navigatorIconCir">{notAnsweredCount}</div>
            )}
            {tab === 'Bookmarks' && (
              <div className="digitalSHSATModule__navigatorIconMark">
                <FaBookmark />
                <div className="digitalSHSATModule__navigatorIconMarkContent">{bookmarkCount}</div>
              </div>
            )}
            <span>{tab}</span>
          </div>
        ))}
      </div>
      <div className="digitalSHSATModule__navigatorBody">
        <div className="digitalSHSATModule__navigatorBodyBox">
          {visible.map((entry) => {
            // Directions ("ELA Rdg Comp Directions") and end (Passage End
            // Directions / End of Section) rows are the dark, clickable info
            // rows; only question rows carry the answered dot / bookmark.
            const isInfo = entry.kind !== 'question';
            return (
              <div
                key={`${entry.kind}-${entry.key}-${entry.label}`}
                className={
                  entry.isCurrent
                    ? 'digitalSHSATModule__navigatorBodyContent--active'
                    : 'digitalSHSATModule__navigatorBodyContent'
                }
                // Info rows get the dark treatment unless they're the current
                // screen (then the standard active highlight applies).
                style={isInfo && !entry.isCurrent ? DARK_ROW_STYLE : undefined}
                onClick={() => onJump(entry)}
              >
                {isInfo ? (
                  <div className="digitalSHSATModule__navigatorQuestionNone" />
                ) : entry.answered ? (
                  <div className="digitalSHSATModule__navigatorQuestionNone" />
                ) : (
                  <div
                    className={
                      entry.isCurrent
                        ? 'digitalSHSATModule__navigatorQuestionCir--active'
                        : 'digitalSHSATModule__navigatorQuestionCir'
                    }
                  />
                )}
                {entry.label}
                {entry.kind === 'question' && entry.bookmarked && (
                  <div className="digitalSHSATModule__navigatorBookmark">
                    <FaBookmark className="digitalSHSATModule__reviewBox--activeIcon" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PassageReviewList;
